import React, { useEffect, useState, useCallback } from "react";
import useContract from "../hooks/useContract";
import { toast } from "react-hot-toast";
import { Contract, formatUnits, JsonRpcProvider } from "ethers";

const RPC_URL = "https://bsc-dataseed.binance.org";

const KIND_ADDRESS = "0x41f52A42091A6B2146561bF05b722Ad1d0e46f8b";
const KIND_BNB_PAIR = "0x628C0fe6DA9854EDf0f3E2C7657af6ecf29C740E";

const Earnings = () => {
  const {
    getUserDashboard,
    getClaimable,
    withdraw,
  } = useContract();

  const [address, setAddress] = useState("");
  const [isConnected, setIsConnected] = useState(false);

  const [data, setData] = useState({
    energyWalletSettled: "0",
    referralWalletSettled: "0",
    totalEnergyClaimable: "0",
    totalReferralClaimable: "0",
    totalClaimableUSD: "0",
    totalWithdrawnUsd: "0",
  });

  const [claimLoading, setClaimLoading] = useState(false);
  const [kindPrice, setKindPrice] = useState(0);
  const [kindTokenAmount, setKindTokenAmount] = useState("0");

  /*
   * ---------------------------------------------------------
   * CHECK WALLET
   * ---------------------------------------------------------
   */
  const checkWallet = useCallback(async () => {
    if (typeof window === "undefined" || !window.ethereum) {
      setAddress("");
      setIsConnected(false);
      return;
    }

    try {
      const accounts = await window.ethereum.request({
        method: "eth_accounts",
      });

      if (accounts && accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);
      } else {
        setAddress("");
        setIsConnected(false);
      }
    } catch (error) {
      console.error("Wallet check error:", error);
      setAddress("");
      setIsConnected(false);
    }
  }, []);

  /*
   * ---------------------------------------------------------
   * CONNECT WALLET
   * ---------------------------------------------------------
   */
  const connectWallet = async () => {
    if (typeof window === "undefined" || !window.ethereum) {
      toast.error("Please install MetaMask or another Web3 wallet");
      return;
    }

    try {
      const accounts = await window.ethereum.request({
        method: "eth_requestAccounts",
      });

      if (accounts && accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);

        toast.success("Wallet connected");

        // Reload earnings immediately
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error("Connect wallet error:", error);

      if (error?.code === 4001) {
        toast.error("Wallet connection rejected");
      } else {
        toast.error("Failed to connect wallet");
      }
    }
  };

  /*
   * ---------------------------------------------------------
   * KIND PRICE
   * ---------------------------------------------------------
   */
  const fetchKindPrice = useCallback(async () => {
    try {
      const provider = new JsonRpcProvider(RPC_URL);

      const pair = new Contract(
        KIND_BNB_PAIR,
        [
          "function getReserves() view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)",
          "function token0() view returns (address)",
          "function token1() view returns (address)",
        ],
        provider
      );

      const [reserves, token0] = await Promise.all([
        pair.getReserves(),
        pair.token0(),
      ]);

      let kindReserve;
      let bnbReserve;

      if (token0.toLowerCase() === KIND_ADDRESS.toLowerCase()) {
        kindReserve = reserves[0];
        bnbReserve = reserves[1];
      } else {
        kindReserve = reserves[1];
        bnbReserve = reserves[0];
      }

      const kindAmount = parseFloat(formatUnits(kindReserve, 18));
      const bnbAmount = parseFloat(formatUnits(bnbReserve, 18));

      if (kindAmount <= 0 || bnbAmount <= 0) {
        return;
      }

      const kindPriceInBNB = bnbAmount / kindAmount;

      const bnbUsdResponse = await fetch(
        "https://api.binance.com/api/v3/ticker/price?symbol=BNBUSDT"
      );

      const bnbUsdData = await bnbUsdResponse.json();
      const bnbUsdPrice = parseFloat(bnbUsdData.price);

      if (!bnbUsdPrice || bnbUsdPrice <= 0) {
        return;
      }

      const kindUsdPrice = kindPriceInBNB * bnbUsdPrice;

      setKindPrice(kindUsdPrice);
    } catch (error) {
      console.error("KIND price fetch error:", error);
    }
  }, []);

  /*
   * ---------------------------------------------------------
   * REFRESH EARNINGS
   * ---------------------------------------------------------
   */
  const refreshEarnings = useCallback(async () => {
    if (!address) return;

    try {
      const [dashboardRes, claimableRes] = await Promise.all([
        getUserDashboard(address),
        getClaimable(address),
      ]);

      const energyWalletSettled = parseFloat(
        dashboardRes?.energyWallet || "0"
      );

      const referralWalletSettled = parseFloat(
        dashboardRes?.referralWallet || "0"
      );

      const totalEnergyClaimable = parseFloat(
        claimableRes?.energy || "0"
      );

      const totalReferralClaimable = parseFloat(
        claimableRes?.referral || "0"
      );

      const totalUSD =
        totalEnergyClaimable + totalReferralClaimable;

      const kindAmount =
        kindPrice > 0 ? totalUSD / kindPrice : 0;

      setData({
        energyWalletSettled: energyWalletSettled.toFixed(4),

        referralWalletSettled: referralWalletSettled.toFixed(4),

        totalEnergyClaimable:
          totalEnergyClaimable.toFixed(4),

        totalReferralClaimable:
          totalReferralClaimable.toFixed(4),

        totalClaimableUSD:
          totalUSD.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),

        totalWithdrawnUsd:
          parseFloat(
            dashboardRes?.totalWithdrawnUsd || "0"
          ).toFixed(2),
      });

      setKindTokenAmount(
        kindAmount.toFixed(6)
      );
    } catch (error) {
      console.error(
        "Earnings fetch error:",
        error
      );

      toast.error(
        "Failed to load earnings data"
      );
    }
  }, [
    address,
    getUserDashboard,
    getClaimable,
    kindPrice,
  ]);

  /*
   * ---------------------------------------------------------
   * CLAIM
   * ---------------------------------------------------------
   */
const handleClaim = async () => {
  if (!address) {
    toast.error("Wallet not connected");
    return;
  }

  try {
    setClaimLoading(true);

    toast.loading(
      "Please confirm the transaction in your wallet...",
      {
        id: "claim",
      }
    );

    // Directly call withdraw
    const txHash = await withdraw();

    console.log("Withdraw transaction:", txHash);

    toast.success(
      "✅ Earnings claimed successfully!",
      {
        id: "claim",
      }
    );

    await refreshEarnings();
  } catch (error) {
    console.error("Claim error:", error);

    const errorMessage =
      error?.reason ||
      error?.shortMessage ||
      error?.info?.error?.message ||
      error?.error?.message ||
      error?.data?.message ||
      error?.message ||
      "Claim failed";

    toast.error(errorMessage, {
      id: "claim",
    });
  } finally {
    setClaimLoading(false);
  }
};

  /*
   * ---------------------------------------------------------
   * INITIAL WALLET CHECK
   * ---------------------------------------------------------
   */
  useEffect(() => {
    checkWallet();
  }, [checkWallet]);

  /*
   * ---------------------------------------------------------
   * WALLET EVENTS
   * ---------------------------------------------------------
   */
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !window.ethereum
    ) {
      return;
    }

    const handleAccountsChanged = (accounts) => {
      if (accounts && accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);
      } else {
        setAddress("");
        setIsConnected(false);
      }
    };

    const handleChainChanged = () => {
      window.location.reload();
    };

    window.ethereum.on(
      "accountsChanged",
      handleAccountsChanged
    );

    window.ethereum.on(
      "chainChanged",
      handleChainChanged
    );

    return () => {
      if (window.ethereum.removeListener) {
        window.ethereum.removeListener(
          "accountsChanged",
          handleAccountsChanged
        );

        window.ethereum.removeListener(
          "chainChanged",
          handleChainChanged
        );
      }
    };
  }, []);

  /*
   * ---------------------------------------------------------
   * KIND PRICE REFRESH
   * ---------------------------------------------------------
   */
  useEffect(() => {
    fetchKindPrice();

    const priceInterval = setInterval(
      fetchKindPrice,
      15000
    );

    return () => {
      clearInterval(priceInterval);
    };
  }, [fetchKindPrice]);

  /*
   * ---------------------------------------------------------
   * EARNINGS REFRESH
   * ---------------------------------------------------------
   */
  useEffect(() => {
    if (!isConnected || !address) {
      return;
    }

    refreshEarnings();

    const interval = setInterval(
      refreshEarnings,
      30000
    );

    return () => {
      clearInterval(interval);
    };
  }, [
    isConnected,
    address,
    refreshEarnings,
  ]);

  /*
   * ---------------------------------------------------------
   * NOT CONNECTED
   * ---------------------------------------------------------
   */
  if (!isConnected) {
    return (
      <div className="flex items-center justify-center min-h-[80vh] bg-[#111111] px-4">
        <div className="w-full max-w-md bg-[#171717] border border-[#2A2A2A] rounded-3xl p-8 text-center shadow-2xl">

          <div className="w-20 h-20 mx-auto mb-6 bg-[#D3AF37]/10 border-4 border-[#D3AF37]/30 rounded-2xl flex items-center justify-center">
            <div className="w-12 h-12 border-2 border-[#D3AF37] border-t-transparent rounded-full animate-spin"></div>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-[#D3AF37] mb-4">
            Connect to Claim
          </h2>

          <p className="text-gray-300 mb-8 text-lg">
            Connect wallet to claim your earnings
          </p>

          <button
            onClick={connectWallet}
            className="w-full bg-[#D3AF37] hover:bg-[#B59410] py-4 px-8 rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 text-white"
          >
            🔗 Connect Wallet
          </button>

        </div>
      </div>
    );
  }

  /*
   * ---------------------------------------------------------
   * CONNECTED PAGE
   * ---------------------------------------------------------
   */
  return (
    <div className="min-h-screen p-4 md:p-6 text-white">

      {/* HEADER */}
      <section className="mb-12">
        <div className="bg-[#171717] border border-[#2A2A2A] p-6 md:p-8 rounded-3xl shadow-2xl">

          <h1 className="text-3xl md:text-5xl font-black text-[#D3AF37] mb-6">
            💰 Earnings Dashboard
          </h1>

          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">

            <div className="flex items-center gap-3 text-green-400 text-sm bg-green-900/50 p-3 rounded-xl">

              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>

              <span>
                {address
                  ? `${address.slice(0, 6)}...${address.slice(-4)}`
                  : "Connected"}
              </span>

            </div>

            <div className="text-[#D3AF37] text-lg font-mono bg-[#D3AF37]/10 px-4 py-2 rounded-xl border border-[#D3AF37]/30">
              KIND: $
              {kindPrice > 0
                ? kindPrice.toFixed(6)
                : "Loading..."}
            </div>

          </div>
        </div>
      </section>

      {/* TOTAL CLAIMABLE */}
      <section className="mb-12">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-[#171717] p-8 rounded-3xl border border-[#2A2A2A] shadow-2xl">

          <div className="text-center">

            <h3 className="text-2xl font-bold text-[#D3AF37] mb-4 flex items-center justify-center gap-3">
              💵 Total Claimable USD
            </h3>

            <div className="text-4xl md:text-5xl font-black text-[#D3AF37] mb-2">
              ${data.totalClaimableUSD}
            </div>

            <div className="text-xl font-bold text-white">
              {parseFloat(kindTokenAmount || "0").toLocaleString()} KIND
            </div>

          </div>

          <div className="text-center">

            <h3 className="text-xl font-bold text-[#D3AF37] mb-4">
              Total Withdrawn
            </h3>

            <div className="text-3xl font-black text-white">
              ${data.totalWithdrawnUsd}
            </div>

          </div>

        </div>

      </section>

      {/* EARNINGS BREAKDOWN */}
      <section className="mb-12">

        <h2 className="text-2xl md:text-3xl font-bold mb-8 text-[#D3AF37] flex items-center gap-3">
          📊 Earnings Breakdown
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

          <EarningsCard
            title="⚡ Energy Wallet"
            value={`$${data.energyWalletSettled}`}
            subtitle={`${parseFloat(
              data.totalEnergyClaimable || "0"
            ).toFixed(4)} total (incl. pending)`}
            color="green"
          />

          <EarningsCard
            title="👥 Referral Wallet"
            value={`$${data.referralWalletSettled}`}
            subtitle={`${parseFloat(
              data.totalReferralClaimable || "0"
            ).toFixed(4)} total`}
            color="purple"
          />

          <EarningsCard
            title="🎁 KIND Payout Preview"
            value={`${parseFloat(
              kindTokenAmount || "0"
            ).toLocaleString()} KIND`}
            subtitle={`@${
              kindPrice > 0
                ? kindPrice.toFixed(6)
                : "0.000000"
            }/KIND`}
            color="yellow"
          />

        </div>

      </section>

      {/* CLAIM */}
      <section className="text-center pb-12">

        <button
          onClick={handleClaim}
          disabled={
            claimLoading ||
            parseFloat(
              data.totalClaimableUSD.replace(/,/g, "")
            ) === 0
          }
          className={`px-16 py-6 text-xl font-bold rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-300 border-4 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed ${
            parseFloat(
              data.totalClaimableUSD.replace(/,/g, "")
            ) === 0
              ? "bg-[#171717] border-[#2A2A2A] text-gray-500"
              : "bg-[#D3AF37] hover:bg-[#B59410] border-[#D3AF37]/50 text-white"
          }`}
        >

          {claimLoading ? (
            <>
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin inline-block mr-3"></div>
              Claiming...
            </>
          ) : parseFloat(
              data.totalClaimableUSD.replace(/,/g, "")
            ) === 0 ? (
            "Nothing to Claim"
          ) : (
            `💰 Claim $${data.totalClaimableUSD}`
          )}

        </button>

      </section>

    </div>
  );
};

/*
 * =========================================================
 * EARNINGS CARD
 * =========================================================
 */
const EarningsCard = ({
  title,
  value,
  subtitle,
  color,
}) => {

  const colors = {
    green: {
      bg: "from-[#18261f] to-[#111111]",
      border: "border-[#2A2A2A]",
      text: "text-[#D3AF37]",
    },

    purple: {
      bg: "from-[#22172d] to-[#111111]",
      border: "border-[#2A2A2A]",
      text: "text-[#D3AF37]",
    },

    yellow: {
      bg: "from-[#2a2412] to-[#111111]",
      border: "border-[#2A2A2A]",
      text: "text-[#D3AF37]",
    },
  };

  const style =
    colors[color] || colors.green;

  return (
    <div
      className={`
        p-8
        rounded-3xl
        bg-gradient-to-br
        ${style.bg}
        ${style.border}
        backdrop-blur-xl
        shadow-2xl
        hover:shadow-3xl
        hover:scale-[1.02]
        transition-all
        duration-300
        border-opacity-70
        group
      `}
    >

      <h3 className="text-lg font-bold mb-4 opacity-90 text-white">
        {title}
      </h3>

      <div
        className={`
          text-4xl
          font-black
          mb-3
          group-hover:scale-105
          transition-transform
          ${style.text}
        `}
      >
        {value}
      </div>

      {subtitle && (
        <p className="text-sm text-gray-400 font-mono bg-black/40 px-3 py-2 rounded-xl inline-block">
          {subtitle}
        </p>
      )}

    </div>
  );
};

export default Earnings;